import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical } from '@phosphor-icons/react';
import {
  Cursor,
  PencilSimple,
  CaretCircleDown,
  ArrowSquareOut,
  Timer,
  ArrowsDownUp,
  Hand,
  UploadSimple,
  Eye,
  ChatCircleText,
  ToggleRight,
  Keyboard,
  ArrowsOutCardinal,
  Crosshair,
  DotsThree,
  Trash,
  Table,
  Terminal,
} from '@phosphor-icons/react';
import type { LegacyPlaybookStepAction, PlaybookStep } from '@chatsdet/types';
import { cn } from '@chatsdet/ui';

function getActionIcon(action: LegacyPlaybookStepAction): React.ReactNode {
  const iconProps = { size: 12, weight: 'fill' as const };
  switch (action) {
    case 'click':
      return <Cursor {...iconProps} />;
    case 'fill':
      return <PencilSimple {...iconProps} />;
    case 'fill_form':
      return <PencilSimple {...iconProps} />;
    case 'select':
      return <CaretCircleDown {...iconProps} />;
    case 'navigate':
      return <ArrowSquareOut {...iconProps} />;
    case 'wait':
      return <Timer {...iconProps} />;
    case 'scroll':
      return <ArrowsDownUp {...iconProps} />;
    case 'hover':
      return <Hand {...iconProps} />;
    case 'upload':
      return <UploadSimple {...iconProps} />;
    case 'verify':
      return <Eye {...iconProps} />;
    case 'handle_native_dialog':
      return <ChatCircleText {...iconProps} />;
    case 'toggle':
      return <ToggleRight {...iconProps} />;
    case 'press_key':
      return <Keyboard {...iconProps} />;
    case 'drag':
      return <ArrowsOutCardinal {...iconProps} />;
    case 'capture':
      return <Crosshair {...iconProps} />;
    case 'extract_table':
      return <Table {...iconProps} />;
    case 'python_execute':
      return <Terminal {...iconProps} />;
    case 'other':
      return <DotsThree {...iconProps} />;
    default:
      return <Cursor {...iconProps} />;
  }
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

interface SortablePlaybookRowProps {
  sortableId: string;
  step: PlaybookStep;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
  onLeave: () => void;
  onDelete: () => void;
  isHovered: boolean;
  /**
   * Read-only mode (the default for the playbook overlay — playbooks are
   * agent-owned, not human-edited). Hides the drag handle + delete button and
   * makes the row non-interactive (no click-to-edit). The rich display (icon,
   * order, action/target/params/note) is unchanged.
   */
  readOnly?: boolean;
}

export function SortablePlaybookRow({
  sortableId,
  step,
  isSelected,
  onSelect,
  onHover,
  onLeave,
  onDelete,
  isHovered,
  readOnly = false,
}: SortablePlaybookRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasParams = step.params && Object.keys(step.params).length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 text-xs transition-colors cursor-default group',
        'hover:bg-muted/60',
        isHovered && 'bg-muted/60',
        isSelected && 'bg-primary/10 ring-1 ring-primary/30'
      )}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={readOnly ? undefined : onSelect}
    >
      {/* Drag handle (edit-only) */}
      {!readOnly && (
        <span
          className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground"
          {...attributes}
          {...listeners}
        >
          <DotsSixVertical size={14} weight="bold" />
        </span>
      )}

      {/* Order badge */}
      <span className="flex items-center justify-center w-5 h-5 rounded bg-muted text-muted-foreground text-[10px] font-mono font-medium shrink-0">
        {step.order}
      </span>

      {/* Action icon */}
      <span className="shrink-0 w-4 text-center text-muted-foreground" aria-hidden>
        {getActionIcon(step.action)}
      </span>

      {/* Content */}
      <span className="min-w-0 flex-1">
        <span className="font-medium">{formatAction(step.action)}</span>{' '}
        <span className="text-muted-foreground">
          {step.target}
          {hasParams &&
            Object.entries(step.params!).map(([key, value]) => (
              <span key={key}>
                {' '}
                (
                <span className="italic">
                  {key}: {value}
                </span>
                )
              </span>
            ))}
          {step.note && <span className="italic ml-1">({step.note})</span>}
        </span>
      </span>

      {/* Delete button (edit-only) */}
      {!readOnly && (
        <button
          type="button"
          className={cn(
            'shrink-0 p-0.5 rounded text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-opacity',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete step"
        >
          <Trash size={14} />
        </button>
      )}
    </div>
  );
}
